/**
 * Clarity One module registry — navigation destinations that can grow without
 * redesigning the shell. Existing modules are live; future ones are reserved.
 *
 * Home cards and header nav should read from this list (or a filtered subset)
 * when adding Service Reports, Care Plans, etc.
 */
export type ClarityOneModuleId =
  | "home"
  | "proposals"
  | "documents"
  | "invoices"
  | "statement"
  | "service"
  | "wallet"
  | "account"
  /** Future — not routed yet */
  | "service_reports"
  | "care_plans"
  | "equipment_inventory"
  | "warranty"
  | "deliveries"
  | "project_photos"
  | "installation_photos";

export type ClarityOneModuleDef = {
  id: ClarityOneModuleId;
  label: string;
  path: string;
  /** When false, module is reserved for a later release (no nav/route yet). */
  enabled: boolean;
  /**
   * When false, the module is live but not shown in the primary header nav
   * (e.g. Wallet lives under My Account).
   */
  primaryNav?: boolean;
  /** Wallet and similar may be gated per customer. */
  conditional?: "wallet_toggle";
};

export const CLARITY_ONE_MODULES: readonly ClarityOneModuleDef[] = [
  { id: "home", label: "Home", path: "/home", enabled: true },
  { id: "proposals", label: "My Projects", path: "/proposals", enabled: true },
  { id: "documents", label: "Project Documents", path: "/documents", enabled: true },
  { id: "invoices", label: "My Invoices", path: "/invoices", enabled: true },
  { id: "statement", label: "Account Statement", path: "/account/statement", enabled: true },
  { id: "service", label: "Service", path: "/service", enabled: true },
  { id: "warranty", label: "Warranty", path: "/warranty", enabled: true },
  { id: "deliveries", label: "Deliveries", path: "/deliveries", enabled: true },
  {
    id: "wallet",
    label: "Kaleidescape Wallet",
    path: "/wallet/kaleidescape",
    enabled: true,
    primaryNav: false,
    conditional: "wallet_toggle",
  },
  { id: "account", label: "My Account", path: "/account", enabled: true },
  { id: "service_reports", label: "Service Reports", path: "/service-reports", enabled: false },
  { id: "care_plans", label: "Care Plans", path: "/care-plans", enabled: false },
  { id: "equipment_inventory", label: "Equipment Inventory", path: "/equipment", enabled: false },
  { id: "project_photos", label: "Project Photos", path: "/photos/project", enabled: false },
  {
    id: "installation_photos",
    label: "Installation Photos",
    path: "/photos/installation",
    enabled: false,
  },
] as const;

export function listEnabledClarityOneModules(): ClarityOneModuleDef[] {
  return CLARITY_ONE_MODULES.filter((m) => m.enabled);
}

/** Modules shown in the primary header navigation. */
export function listPrimaryClarityOneNavModules(): ClarityOneModuleDef[] {
  return listEnabledClarityOneModules().filter((m) => m.primaryNav !== false);
}
